import * as Sentry from "@sentry/react";
import qs from "query-string";
import { create } from "zustand";

import { getToken, isAuthenticated } from "../../../app/components/api/auth";
import { safeJsonParse } from "../../../app/components/global/JsonHelper";
import { invalidateSession } from "../../../app/components/ui library/helpers/functions";
import { shouldUpdateTokenExpiration } from "../../../app/components/ui library/helpers/sessionFunctions";
import { exceptions403List } from "../../../app/components/ui library/helpers/strings";

import { toSnakeCaseKeys } from "./_utils";
import { initializeModels } from "./model-registry";

const baseUrl = process.env.REACT_APP_API_URL || "https://api.healthnsync.com";

export async function getPnAccessTokenFromCookie() {
  if (!isAuthenticated()) {
    await invalidateSession();
    return null;
  }

  return getToken();
}

async function getConfig() {
  const token = await getPnAccessTokenFromCookie();
  return {
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Token ${token}` }),
    },
    mode: "cors",
  };
}

async function toFormData(obj, form = new FormData(), namespace = "") {
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) {
      const newNamespace = `${namespace}${key}`;
      form.append(newNamespace, null);
      continue;
    }
    const isArray = Array.isArray(value);
    const isObject =
      typeof value === "object" && !(value instanceof File) && !(value instanceof Date);

    if (isArray) {
      value.forEach((element, index) => {
        const newNamespace = namespace ? `${namespace}${key}[${index}]` : `${key}[${index}]`;

        if (typeof element === "object" && !(element instanceof File)) {
          toFormData(element, form, newNamespace);
        } else {
          form.append(newNamespace, element);
        }
      });
    } else if (isObject) {
      const newNamespace = namespace ? `${namespace}${key}` : key;
      toFormData(value, form, newNamespace);
    } else {
      const formKey = namespace ? `${namespace}${key}` : key;
      form.append(formKey, value);
    }
  }
  return form;
}

async function fetchWithInterceptors(url, options = {}) {
  const allowedSources = ["healthnsyncinfra.net", "healthnsync.com", "netproapp.com"];

  const isTrustedSource = (url) => {
    if (!url) {
      return false;
    }
    try {
      const hostname = new URL(url).hostname;
      return allowedSources.some((domain) => hostname.endsWith(domain));
    } catch {
      return false;
    }
  };

  const response = await fetch(url, options);
  const text = await response.text();
  const data = text ? safeJsonParse(text, {}) : {};

  if (!response.ok) {
    if (isTrustedSource(url)) {
      if (response.status === 401) {
        console.warn("401 Unauthorized:", options?.method || "GET", url);
        Sentry.captureMessage("401 Unauthorized", {
          level: "info",
          extra: {
            url,
            method: options?.method || "GET",
          },
        });
        await invalidateSession();
      } else if (
        response.status === 403 &&
        !exceptions403List.some((exception) => url.includes(exception))
      ) {
        const err = new Error(`403 Forbidden on ${url}`);
        Sentry.captureException(err);
      }
    }

    const errorObj = {
      status: response.status,
      description: response?.statusText || "Unknown error",
      details: data ?? null,
      url,
      method: options?.method || "GET",
    };

    throw errorObj;
  }

  if (response?.headers) {
    response.headers.forEach((value, key) => {
      if (key === "x-token-expiry" && shouldUpdateTokenExpiration(value)) {
        localStorage.setItem("token_exp", value);
      }
    });
  }

  return data;
}

async function getModel(type) {
  const modelRegistry = await initializeModels();
  const Model = modelRegistry[type];
  if (!Model) {
    throw new Error(`No model registered for type: ${type}`);
  }

  return { modelRegistry, Model };
}

const useStore = create((set, get) => ({
  records: {},
  totalCounts: {},
  nextPage: {},
  rawData: {},

  appendRecord: (type, record) => {
    set((state) => {
      const existing = state.records[type];
      const normalizedArray = Array.isArray(existing) ? existing : existing ? [existing] : [];
      return {
        records: {
          ...state.records,
          [type]: [...normalizedArray, record],
        },
      };
    });
  },

  setRawData: (type, data) => {
    set((state) => ({
      rawData: {
        ...state.rawData,
        [type]: data,
      },
    }));
  },

  setTotalCount: (type, count) => {
    set((state) => ({
      totalCounts: {
        ...state.totalCounts,
        [type]: count,
      },
    }));
  },

  setNextPage: (type, nextPage) => {
    set((state) => ({
      nextPage: {
        ...state.nextPage,
        [type]: nextPage,
      },
    }));
  },

  setRecords: async (type, data) => {
    const { modelRegistry, Model } = await getModel(type);
    set((state) => ({
      records: {
        ...state.records,
        [type]: Array.isArray(data)
          ? data.map((item) => Model.fromJson(item, type, { modelRegistry }))
          : Model.fromJson(data, type, { modelRegistry }),
      },
    }));
  },

  getRawData: (type) => {
    return get().rawData[type] || 0;
  },

  getTotalCount: (type) => {
    return get().totalCounts[type] || 0;
  },

  getNextPage: (type) => {
    return get().nextPage[type] || null;
  },

  getOptions: async (type) => {
    const { modelRegistry, Model } = await getModel(type);
    const urlPath = Model.urlPath ?? type;
    const data = await fetchWithInterceptors(`${baseUrl}/api/${urlPath}`, {
      method: "OPTIONS",
      ...(await getConfig()),
    });
    const OptionsModel = modelRegistry[`${type}-options`];
    const record = OptionsModel.fromJson(data, type, { modelRegistry });
    await get().appendRecord(type, record);
    return record;
  },

  get: async (type) => {
    const { modelRegistry, Model } = await getModel(type);
    const urlPath = Model.urlPath ?? type;
    const data = await fetchWithInterceptors(`${baseUrl}/api/${urlPath}`, {
      ...(await getConfig()),
    });
    const record = Model.fromJson(data, type, { modelRegistry });
    await get().appendRecord(type, record);
    return record;
  },

  findAll: async (type, params = {}) => {
    const { Model } = await getModel(type);
    const urlPath = Model.urlPath ?? type;
    const requestUrl =
      "urlPath" in params
        ? `${baseUrl}/api/${params.urlPath}`
        : `${baseUrl}/api/${urlPath}/?${qs.stringify(params)}`;

    const data = await fetchWithInterceptors(requestUrl, {
      ...(await getConfig()),
    });

    await get().setRawData(type, data);
    await get().setRecords(type, data?.results ?? data);
    await get().setTotalCount(type, data?.count ?? 0);
    await get().setNextPage(type, data?.next ?? null);
    return get().records[type];
  },

  patch: async (type, id, body, settings) => {
    const { modelRegistry, Model } = await getModel(type);
    const urlPath = Model.urlPath ?? type;
    const requestUrl = settings?.urlPath
      ? `${baseUrl}/api/${settings.urlPath}`
      : `${baseUrl}/api/${urlPath}/${id}/`;
    const requestOptions = {
      method: "PATCH",
      ...(await getConfig()),
    };

    if (body instanceof FormData) {
      delete requestOptions.headers["Content-Type"];
      requestOptions.body = body;
    } else {
      requestOptions.body = JSON.stringify(Model?.safe ? body : toSnakeCaseKeys(body));
    }

    const data = await fetchWithInterceptors(requestUrl, requestOptions);
    const record = Model.fromJson(data, type, { modelRegistry });
    await get().appendRecord(type, record);
    return record;
  },

  findRecord: async (type, id, options = {}) => {
    const { modelRegistry, Model } = await getModel(type);
    if (!options?.noCache) {
      const existing = get().records[type];
      const cachedRecords = Array.isArray(existing) ? existing : existing ? [existing] : [];
      const cached = cachedRecords.find((item) => item.id === parseInt(id));
      if (cached) {
        return cached;
      }
    }

    const urlPath = Model.urlPath ?? type;
    const requestUrl =
      "urlPath" in options
        ? `${baseUrl}/api/${options.urlPath}`
        : `${baseUrl}/api/${urlPath}/${id}/`;
    const data = await fetchWithInterceptors(requestUrl, {
      ...(await getConfig()),
    });
    const record = Model.fromJson(data, type, { modelRegistry });
    await get().appendRecord(type, record);
    return record;
  },

  peekRecord: async (type, id) => {
    await getModel(type);
    const existing = get().records[type];
    const cachedRecords = Array.isArray(existing) ? existing : existing ? [existing] : [];
    const cached = cachedRecords.find((item) => item.id === parseInt(id));
    if (cached) {
      return cached;
    }
  },

  createRecord: async (type, payload) => {
    const { Model } = await getModel(type);
    const instance = new Model();

    Object.assign(instance, {
      __type: type,
      ...payload,
    });
    return instance;
  },

  createBulk: async (type, payload, settings) => {
    if (!Array.isArray(payload)) {
      throw new Error("Payload must be an Array");
    }

    const { modelRegistry, Model } = await getModel(type);
    const urlPath = Model.urlPath ?? type;
    const requestUrl =
      "urlPath" in settings ? `${baseUrl}/api/${settings.urlPath}/` : `${baseUrl}/api/${urlPath}/`;

    const requestOptions = {
      method: "POST",
      body: JSON.stringify(payload.map(toSnakeCaseKeys)),
      ...(await getConfig()),
    };

    const data = await fetchWithInterceptors(requestUrl, requestOptions);

    const records = (data?.successes ?? data).map((item) =>
      Model.fromJson(item, type, { modelRegistry })
    );

    await get().setRecords(type, records);
    return records;
  },

  __save: async (instance, settings) => {
    const type = instance.__type;
    const { modelRegistry } = await getModel(type);
    const urlPath = instance.constructor.urlPath ?? type;
    const requestUrl =
      "urlPathWithQueryParams" in settings
        ? `${baseUrl}/api/${settings.urlPathWithQueryParams}`
        : "urlPath" in settings
          ? `${baseUrl}/api/${settings.urlPath}/`
          : `${baseUrl}/api/${urlPath}/`;
    const data = await fetchWithInterceptors(requestUrl, {
      method: "POST",
      body: JSON.stringify(toSnakeCaseKeys(instance)), // Ensures data transformation
      ...(await getConfig()),
    });

    const record = instance.constructor.fromJson(data, instance.__type, {
      modelRegistry,
    });
    await get().appendRecord(type, record);
    return record;
  },

  updateRecord: async (type, id, updates) => {
    const { modelRegistry, Model } = await getModel(type);
    const updatedInstance = new Model({ ...updates, __type: type, id });
    const urlPath = Model.urlPath ?? type;
    const data = await fetchWithInterceptors(`${baseUrl}/api/${urlPath}/${id}`, {
      method: "PUT",
      body: updatedInstance.serialize(),
      ...(await getConfig()),
    });
    const updatedRecord = Model.fromJson(data, { modelRegistry });
    set((state) => {
      const existing = state.records[type];
      const normalizedArray = Array.isArray(existing) ? existing : existing ? [existing] : [];
      return {
        records: {
          ...state.records,
          [type]: normalizedArray.map((item) => (item.id === id ? updatedRecord : item)),
        },
      };
    });
    return updatedRecord;
  },

  deleteRecord: async (type, id, settings = {}) => {
    const { Model } = await getModel(type);
    const urlPath = Model.urlPath ?? type;
    const requestUrl =
      "urlPath" in settings
        ? `${baseUrl}/api/${settings.urlPath}/${id}/`
        : `${baseUrl}/api/${urlPath}/${id}/`;
    await fetchWithInterceptors(requestUrl, {
      method: "DELETE",
      ...(await getConfig()),
    });

    set((state) => {
      const existing = state.records[type];
      const normalizedArray = Array.isArray(existing) ? existing : existing ? [existing] : [];
      const normalizedId = String(id);

      return {
        records: {
          ...state.records,
          [type]: normalizedArray.filter((item) => String(item?.id) !== normalizedId),
        },
      };
    });
  },

  async upload(type, body, params = {}, method = "PATCH") {
    if (params.isExternalPath) {
      const response = await fetch(params.urlPath, {
        method,
        body: new Blob([body]),
        headers: {
          "Content-Type": body.type || "application/octet-stream",
        },
      });

      return response;
    }

    const { modelRegistry, Model } = await getModel(type);
    const urlPath = Model.urlPath ?? type;
    const requestUrl =
      "urlPath" in params ? `${baseUrl}/api/${params.urlPath}` : `${baseUrl}/api/${urlPath}/`;

    const { headers, mode } = await getConfig();
    delete headers["Content-Type"];

    const formData = await toFormData(body);

    const data = await fetchWithInterceptors(requestUrl, {
      method,
      body: formData,
      headers: headers,
      mode: mode,
    });

    const record = Model.fromJson(data, type, { modelRegistry });
    await get().appendRecord(type, record);
    return record;
  },

  async post(type, payload, settings = {}) {
    const { Model } = await getModel(type);
    const urlPath = Model.urlPath ?? type;

    const requestUrl =
      "urlPath" in settings ? `${baseUrl}/api/${settings.urlPath}` : `${baseUrl}/api/${urlPath}/`;

    return await fetchWithInterceptors(
      requestUrl,
      {
        method: "POST",
        body: JSON.stringify(Model?.safe ? payload : toSnakeCaseKeys(payload)),
        ...(await getConfig()),
      },
      { withStatus: settings?.withStatus ?? false }
    );
  },
}));

export default useStore;
